// Copyright ©️ 2026 eVolve MEP, LLC

import './work-order-detail.css';

import {
  type QrCodeResponse,
  type WorkOrder,
  useEvolveGet,
  useEvolvePaginatedGet,
  type WorkOrderPaginated,
} from '@evolvemep/api-ts';
import { Flex, Loader, Stack, Table, Text, Image as MantineImage, Card, Title } from '@mantine/core';

import type { ModalRegistryManagedProps } from 'components/Mantine/Modals/createModalRegistry';
import { PrintableModal, PrintableTableFrame } from 'components/Mantine/Modals/PrintableModal';
import { formatQuantity } from 'helpers/formatQuantity';
import { useGetAllDataFromFetcher } from 'helpers/getAllDataFromFetcher';
import { isNil } from 'helpers/isNotNil';
import { useUnitOfMeasure } from 'hooks/typeCacheHooks/useUnitOfMeasure';

export type { QrCodeResponse };

type Props = ModalRegistryManagedProps & {
  workOrder: WorkOrder | WorkOrderPaginated;
};

const WorkOrderTravelerContents = ({ workOrder }: Pick<Props, 'workOrder'>) => {
  const { uoms } = useUnitOfMeasure();

  const { data: qrCodeResponse } = useEvolveGet(`shop/workOrder/${workOrder.workOrderId}/qrCode`, {
    lazy: false,
  });

  const { fetchPage } = useEvolvePaginatedGet(`shop/workOrder/item`);
  const [data, loading] = useGetAllDataFromFetcher(fetchPage, {
    params: {
      workOrderId: workOrder.workOrderId,
      orderBy: 'name:asc',
    },
  });

  if (isNil(qrCodeResponse) || loading) {
    return (
      <Stack align="center" p="xl">
        <Loader />
      </Stack>
    );
  }

  return (
    <>
      <Card withBorder my="xs">
        <Flex justify="space-between" align="center" gap="xs">
          <Stack gap={0}>
            <Title order={2}>{workOrder.identifier}</Title>
            <Text fw={500} fz="xl">
              {workOrder.name}{' '}
              <Text span c="dimmed">
                ({data.length} items)
              </Text>
            </Text>
            <Text size="sm" c="dimmed">
              {workOrder.projectName}
            </Text>
          </Stack>
          <MantineImage
            src={`data:image/svg+xml;base64,${qrCodeResponse.rawData}`}
            alt="Work order traveler QR code"
            maw={90}
          />
        </Flex>
      </Card>
      <PrintableTableFrame>
        <Table withTableBorder stickyHeader>
          <Table.Thead>
            <Table.Tr>
              <Table.Th bg="gray.1">Item Name</Table.Th>
              <Table.Th bg="gray.1">Total Qty</Table.Th>
              <Table.Th bg="gray.1">UOM</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.map((item) => (
              <Table.Tr key={item.workOrderItemId}>
                <Table.Td>{item.name}</Table.Td>
                <Table.Td>{formatQuantity(item.quantity)}</Table.Td>
                <Table.Td>{uoms?.find((u) => u.unitOfMeasureId === item.unitOfMeasureId)?.name ?? ''}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </PrintableTableFrame>
    </>
  );
};

export const WorkOrderTravelerModal = ({ workOrder, ...props }: Props) => (
  <PrintableModal title="Print work order traveler" {...props}>
    <WorkOrderTravelerContents workOrder={workOrder} />
  </PrintableModal>
);
